import React, { useState } from 'react';
import { emailAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';

export default function AdvancedSearch({ open, onClose, onResults }) {
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState({
    q: '',
    from_addr: '',
    to_addr: '',
    subject: '',
    date_from: '',
    date_to: '',
    has_attachments: '',
    is_read: '',
    is_flagged: '',
    folder: '',
  });

  const handleSearch = async () => {
    setLoading(true);
    try {
      const searchParams = {};
      Object.entries(params).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) {
          if (k === 'has_attachments' || k === 'is_read' || k === 'is_flagged') {
            searchParams[k] = v === 'true';
          } else {
            searchParams[k] = v;
          }
        }
      });
      const res = await emailAPI.advancedSearch(searchParams);
      onResults(res.data.emails, res.data.total);
      toast.success(`${res.data.total} résultat(s) trouvé(s)`);
    } catch {
      toast.error('Erreur de recherche');
    } finally { setLoading(false); }
  };

  const handleReset = () => {
    setParams({ q: '', from_addr: '', to_addr: '', subject: '', date_from: '', date_to: '', has_attachments: '', is_read: '', is_flagged: '', folder: '' });
  };

  const set = (key, val) => setParams(p => ({ ...p, [key]: val }));

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[380px] sm:w-[420px]" data-testid="advanced-search-panel">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4" /> Recherche avancée
          </SheetTitle>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label>Recherche générale</Label>
            <Input value={params.q} onChange={(e) => set('q', e.target.value)} placeholder="Mot-clé dans sujet, expéditeur ou contenu..." data-testid="search-q-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Expéditeur</Label>
              <Input value={params.from_addr} onChange={(e) => set('from_addr', e.target.value)} placeholder="email@..." className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Destinataire</Label>
              <Input value={params.to_addr} onChange={(e) => set('to_addr', e.target.value)} placeholder="email@..." className="h-8 text-sm" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Sujet</Label>
            <Input value={params.subject} onChange={(e) => set('subject', e.target.value)} placeholder="Contient..." className="h-8 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date début</Label>
              <Input type="date" value={params.date_from} onChange={(e) => set('date_from', e.target.value)} className="h-8 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Date fin</Label>
              <Input type="date" value={params.date_to} onChange={(e) => set('date_to', e.target.value)} className="h-8 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Pièces jointes</Label>
              <Select value={params.has_attachments} onValueChange={(v) => set('has_attachments', v)}>
                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_att">Tous</SelectItem>
                  <SelectItem value="true">Avec</SelectItem>
                  <SelectItem value="false">Sans</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Dossier</Label>
              <Select value={params.folder} onValueChange={(v) => set('folder', v)}>
                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_folders">Tous les dossiers</SelectItem>
                  <SelectItem value="INBOX">Boîte de réception</SelectItem>
                  <SelectItem value="Sent">Envoyés</SelectItem>
                  <SelectItem value="Drafts">Brouillons</SelectItem>
                  <SelectItem value="Trash">Corbeille</SelectItem>
                  <SelectItem value="Spam">Spam</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Statut lecture</Label>
              <Select value={params.is_read} onValueChange={(v) => set('is_read', v)}>
                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_read">Tous</SelectItem>
                  <SelectItem value="false">Non lus</SelectItem>
                  <SelectItem value="true">Lus</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Important</Label>
              <Select value={params.is_flagged} onValueChange={(v) => set('is_flagged', v)}>
                <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Tous" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all_flag">Tous</SelectItem>
                  <SelectItem value="true">Importants</SelectItem>
                  <SelectItem value="false">Non importants</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <SheetFooter className="mt-6 flex gap-2">
          <Button variant="outline" onClick={handleReset} className="flex-1" data-testid="search-reset-btn">
            <X className="w-4 h-4 mr-1" /> Réinitialiser
          </Button>
          <Button onClick={handleSearch} disabled={loading} className="flex-1" data-testid="search-submit-btn">
            <Search className="w-4 h-4 mr-1" /> {loading ? 'Recherche...' : 'Rechercher'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
